import {useEffect,useRef,useState} from 'react';
import {useRadarLocale} from '@/i18n/RadarLocaleContext';
import {optionalRequestId} from '@/lib/requestId';
import {radarApi,type CampaignProfile,type CampaignStart,type ListCampaign} from './radarApi';
import type {GmailConnection} from './types';
import {RadarGmailConnection} from './RadarGmailConnection';

const emptyProfile:CampaignProfile={sender_name:'',seller_company:'',claims:'',objective:'',signature:'',language:'en',tone:'concise',email_template:''};
const pauseReasons:Record<string,string>={
 outcome_allowance_exhausted:'This allowance is used up. Open Usage & plan to check your balance.',
 outcome_trial_expired:'Your trial has ended. Your existing results are still available.',
 usage_work_in_progress:'This company is already being processed. Wait for its result before retrying.',
 usage_exhausted:'This allowance is used up. Open Usage & plan to check your balance.',
 trial_expired:'Your trial has ended. Your existing results are still available.',
 gmail_reconnect:'Reconnect Gmail before preparing more drafts. Your saved work is preserved.',
 history_incomplete:'Gmail could not be fully checked. Refresh and try again before continuing.',
 budget_limit:'Research paused at its processing limit. Your saved work is preserved.',
 scope_changed:'Your Market Brief changed. Review the list before resuming.',
 jobs_cancelled:'Your Market Brief or permissions changed. Review the list before resuming.',
};

// Mounted with a workspace/list key by Research settings. Only an explicit
// approved start/control action queues work; loading this component never does.
export function RadarListCampaign({token,workspaceId,listId,listName,onChanged}:{
 token:string;workspaceId:string;listId:string;listName:string;onChanged?:()=>void;
}) {
 const {t}=useRadarLocale();
 const [campaign,setCampaign]=useState<ListCampaign|null>(null),[connection,setConnection]=useState<GmailConnection>();
 const [profile,setProfile]=useState<CampaignProfile>(emptyProfile),[mode,setMode]=useState<'intro'|'buyer_led'>('intro');
 const [approved,setApproved]=useState(false),[loaded,setLoaded]=useState(false),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const alive=useRef(false),lock=useRef(false),revision=useRef(0);
 const retry=useRef<{fingerprint:string;request:CampaignStart}>();
 useEffect(()=>{setApproved(false);},[connection?.account_email,connection?.draft_access,connection?.available]);
 async function refresh() {
   const current=++revision.current;
   const [next,outreach]=await Promise.all([radarApi.listCampaign(token,workspaceId,listId),radarApi.outreach(token,workspaceId)]);
   if(!alive.current||current!==revision.current)return;
   setCampaign(next);setConnection(outreach.gmail_connection);setLoaded(true);setError('');
 }
 useEffect(()=>{
   alive.current=true;
   void refresh().catch(e=>{if(alive.current)setError(e instanceof Error?e.message:'Could not load list progress.');});
   return()=>{alive.current=false;revision.current++;};
 },[token,workspaceId,listId]);
 useEffect(()=>{
   if(!campaign||!['running','queued'].includes(campaign.status))return;
   const timer=window.setInterval(()=>{
     if(document.hidden||lock.current)return;
     void refresh().catch(e=>{if(alive.current)setError(e instanceof Error?e.message:'Could not refresh list progress.');});
   },10_000);
   return()=>window.clearInterval(timer);
 },[campaign?.status,token,workspaceId,listId]);
 async function action(call:()=>Promise<ListCampaign>) {
   if(lock.current)return;
   lock.current=true;revision.current++;setBusy(true);setError('');
   try{
     const next=await call();
     if(alive.current){setCampaign(next);onChanged?.();}
   }catch(e){if(alive.current)setError(e instanceof Error?e.message:'Please try again.');}
   finally{lock.current=false;if(alive.current)setBusy(false);}
 }
 function change(key:keyof CampaignProfile,value:string){setProfile(p=>({...p,[key]:value}));setApproved(false);}
 async function start() {
   if(!valid||!connection?.account_email)return;
   const input={list_id:listId,profile,delivery:'gmail_drafts' as const,outreach_mode:mode,
     require_buyer_matches:mode==='buyer_led',template_approved:true as const,gmail_account_email:connection.account_email};
   const fingerprint=JSON.stringify(input);
   if(!retry.current||retry.current.fingerprint!==fingerprint){
     const id=optionalRequestId();
     if(!id){setError('Use a secure browser connection to start this work.');return;}
     retry.current={fingerprint,request:{...input,id}};
   }
   const request=retry.current.request;
   await action(()=>radarApi.startListCampaign(token,workspaceId,request));
 }
 const valid=loaded&&approved&&connection?.available&&connection.draft_access&&connection.account_email
   &&profile.sender_name.trim()&&profile.seller_company.trim()&&profile.claims.trim()&&profile.objective.trim()&&profile.email_template.trim().length>=20;
 const field=(key:keyof CampaignProfile,label:string,max:number,multiline=false)=><label className="radar-field radar-field-wide"><span>{t(label)}</span>
   {multiline?<textarea rows={key==='email_template'?6:3} maxLength={max} disabled={busy} value={profile[key]} onChange={e=>change(key,e.target.value)}/>
    :<input maxLength={max} disabled={busy} value={profile[key]} onChange={e=>change(key,e.target.value)}/>}</label>;
 return <section className="radar-list-campaign" aria-label={t('List drafts')}>
   <h3>{t('List drafts')} · {listName}</h3>
   <p>{t('Prepare emails for the companies you selected. Review, edit and send from Gmail. No email is sent automatically.')}</p>
   <RadarGmailConnection token={token} workspaceId={workspaceId} connection={connection} onConnected={async()=>{setApproved(false);await refresh();}}/>
   {!loaded?<p role="status">{t('Loading list progress…')}</p>:campaign?<>
     <div role="status">
       <p><strong>{t(({queued:'Queued',running:'In progress',paused:'Paused',cancelled:'Cancelled',completed:'Finished'} as Record<string,string>)[campaign.status]||'In progress')}</strong></p>
       <p>{t('{processed} / {total} companies processed',{processed:campaign.processed_count,total:campaign.total_count})}</p>
       <p>{t('{count} drafts saved to Gmail',{count:campaign.saved_count})}</p>
       {campaign.pause_reason&&<p>{t(pauseReasons[campaign.pause_reason]||'Work is paused. Review progress and your Gmail connection before resuming.')}</p>}
     </div>
     <p className="radar-muted">{t('Prepared drafts and contact history are available in Act. Remaining work keeps its place when paused.')}</p>
     <div className="radar-form-actions">
       {campaign.status==='paused'&&<button className="radar-primary-action" disabled={busy} onClick={()=>void action(()=>radarApi.controlListCampaign(token,workspaceId,campaign.id,'resume'))}>{t('Resume list')}</button>}
       {['queued','running'].includes(campaign.status)&&<button className="radar-secondary-action" disabled={busy} onClick={()=>void action(()=>radarApi.controlListCampaign(token,workspaceId,campaign.id,'pause'))}>{t('Pause list')}</button>}
       {['queued','running','paused'].includes(campaign.status)&&<button className="radar-text-button" disabled={busy} onClick={()=>void action(()=>radarApi.controlListCampaign(token,workspaceId,campaign.id,'cancel'))}>{t('Cancel remaining work')}</button>}
     </div>
   </>:<>
     <div className="radar-brief-grid">
       {field('sender_name','Your name',100)}{field('seller_company','Your company',200)}
       {field('claims','Your offer',2000,true)}{field('objective','Next step to offer',500)}
       {field('signature','Signature',500,true)}
       <label className="radar-field"><span>{t('Language')}</span><select value={profile.language} disabled={busy} onChange={e=>change('language',e.target.value)}>
         <option value="en">English</option><option value="zh-Hans">简体中文</option><option value="zh-Hant">繁體中文</option></select></label>
       <label className="radar-field"><span>{t('Outreach approach')}</span><select value={mode} disabled={busy} onChange={e=>{setMode(e.target.value as typeof mode);setApproved(false);}}>
         <option value="intro">{t('Introduce my products or services')}</option><option value="buyer_led">{t('Offer buyer research to these companies')}</option></select></label>
       {mode==='buyer_led'&&<p className="radar-muted">{t('Use this only when your offer is buyer research. A draft requires verified buyer opportunities for each target company.')}</p>}
       {field('email_template','Email template',6000,true)}
     </div>
     <p className="radar-muted">{t('Uses your available company, email and draft allowances. Work pauses when an allowance runs out. Existing results remain available.')}</p>
     <label className="radar-campaign-approval"><input type="checkbox" checked={approved} disabled={busy} onChange={e=>setApproved(e.target.checked)}/><span>{t('I approve this template and the selected Gmail account.')}</span></label>
     <button className="radar-primary-action" disabled={busy||!valid} onClick={()=>void start()}>{t(busy?'Starting…':'Start list drafts')}</button>
   </>}
   {error&&<p role="alert">{t(error)}</p>}
   <button className="radar-text-button" disabled={busy} onClick={()=>void refresh().catch(e=>{if(alive.current)setError(e instanceof Error?e.message:'Please try again.');})}>{t('Refresh progress')}</button>
 </section>;
}
